import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

const Section = ({ nr, title, children }) => (
  <div>
    <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
      <span className="w-6 h-6 rounded-md bg-[#C9A84C]/15 border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] text-xs font-black shrink-0">{nr}</span>
      {title}
    </h2>
    <div className="text-zinc-400 text-sm leading-relaxed space-y-2 pl-8">{children}</div>
  </div>
);

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FitSmith" className="h-7 w-7 object-contain" />
            <span className="text-white font-bold tracking-wide">FitSmith</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-14">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center shrink-0">
            <Shield size={20} className="text-[#C9A84C]" />
          </div>
          <div>
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-[3px] mb-1">Juridisch</p>
            <h1 className="text-white font-bold text-3xl sm:text-4xl">Privacy Policy</h1>
          </div>
        </div>
        <p className="text-zinc-600 text-sm mb-10 pl-14">Versie januari 2026 · FitSmith · AVG-conform</p>

        <div className="bg-[#0f0f0f] border border-[#C9A84C]/15 rounded-2xl px-6 py-5 mb-8">
          <p className="text-zinc-300 text-sm leading-relaxed">
            FitSmith hecht veel waarde aan de bescherming van jouw persoonsgegevens. In dit privacybeleid leggen wij uit welke gegevens wij verzamelen, waarom en hoe wij daarmee omgaan. Wij handelen conform de <strong className="text-white">Algemene Verordening Gegevensbescherming (AVG/GDPR)</strong>.
          </p>
        </div>

        <div className="space-y-8">

          <Section nr="1" title="Welke gegevens wij verzamelen">
            <p>Wij verzamelen alleen gegevens die nodig zijn voor onze dienstverlening:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {[
                { label: "Naam", desc: "Voor- en achternaam" },
                { label: "E-mailadres", desc: "Voor communicatie en toegang" },
                { label: "Betalingsgegevens", desc: "Verwerkt via Mollie (niet bij ons opgeslagen)" },
                { label: "Accountgegevens", desc: "Inloggegevens en accountinstellingen" },
                { label: "Intakeformulier", desc: "Persoonlijke fitnessinformatie (bij persoonlijk schema)" },
                { label: "Gebruiksgegevens", desc: "Hoe je de website gebruikt (anoniem via cookies)" },
              ].map(({ label, desc }) => (
                <div key={label} className="bg-[#111] border border-white/6 rounded-xl px-4 py-3">
                  <p className="text-white font-semibold text-sm mb-0.5">{label}</p>
                  <p className="text-zinc-500 text-xs">{desc}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section nr="2" title="Waarom wij deze gegevens verzamelen">
            <p>Wij verwerken jouw gegevens voor de volgende doeleinden:</p>
            <ul className="space-y-1.5 list-none">
              {[
                "Het uitvoeren van de overeenkomst: levering van trainingsschema's, cursussen en coaching",
                "Communicatie over jouw bestelling, account of schema",
                "Versturen van bevestigingsemails en intake-uitnodigingen",
                "Het verbeteren van onze website en diensten",
                "Voldoen aan wettelijke verplichtingen (zoals belastingadministratie)",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p>
              De wettelijke grondslagen voor verwerking zijn: uitvoering van een overeenkomst (art. 6 lid 1 sub b AVG), wettelijke verplichting (sub c), en gerechtvaardigd belang (sub f).
            </p>
          </Section>

          <Section nr="3" title="Hoe lang wij gegevens bewaren">
            <p>
              Wij bewaren jouw gegevens niet langer dan noodzakelijk voor de doeleinden waarvoor zij zijn verzameld. Concreet:
            </p>
            <ul className="space-y-1.5 list-none">
              {[
                "Ordergegevens: 7 jaar (wettelijke belastingbewaarplicht)",
                "Accountgegevens: zolang je account actief is, daarna maximaal 1 jaar",
                "Communicatie (e-mails): maximaal 2 jaar",
                "Analytische cookies: maximaal 13 maanden",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </Section>

          <Section nr="4" title="Delen van gegevens">
            <p>
              FitSmith verkoopt jouw gegevens <strong className="text-white">nooit</strong> aan derden. Wij delen gegevens alleen indien strikt noodzakelijk:
            </p>
            <ul className="space-y-1.5 list-none">
              {[
                "Mollie B.V. — betalingsverwerking (eigen privacybeleid van toepassing)",
                "Resend — verzenden van transactionele e-mails",
                "Hostingproviders — voor het draaien van onze website en database",
                "Overheidsinstanties — indien wettelijk verplicht",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p>
              Met verwerkers die namens ons persoonsgegevens verwerken, sluiten wij verwerkersovereenkomsten conform de AVG.
            </p>
          </Section>

          <Section nr="5" title="Beveiliging van gegevens">
            <p>FitSmith neemt passende technische en organisatorische maatregelen ter bescherming van jouw gegevens:</p>
            <ul className="space-y-1.5 list-none">
              {[
                "Alle verbindingen zijn beveiligd met SSL/TLS-encryptie (HTTPS)",
                "Wachtwoorden worden gehashed opgeslagen (bcrypt)",
                "Toegang tot persoonsgegevens is beperkt tot bevoegde medewerkers",
                "Betalingsgegevens worden verwerkt via de beveiligde omgeving van Mollie en nooit door ons opgeslagen",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </Section>

          <Section nr="6" title="Jouw rechten">
            <p>Op grond van de AVG heb je de volgende rechten met betrekking tot jouw persoonsgegevens:</p>
            <div className="space-y-2">
              {[
                { right: "Recht op inzage", desc: "Je kunt opvragen welke gegevens wij van je verwerken." },
                { right: "Recht op rectificatie", desc: "Je kunt onjuiste gegevens laten corrigeren." },
                { right: "Recht op verwijdering", desc: "Je kunt verzoeken jouw gegevens te verwijderen, tenzij wettelijke bewaarplicht geldt." },
                { right: "Recht op beperking", desc: "Je kunt de verwerking van jouw gegevens (tijdelijk) laten beperken." },
                { right: "Recht op bezwaar", desc: "Je kunt bezwaar maken tegen verwerking op basis van gerechtvaardigd belang." },
                { right: "Recht op overdraagbaarheid", desc: "Je kunt jouw gegevens in een gangbaar formaat opvragen." },
              ].map(({ right, desc }) => (
                <div key={right} className="bg-[#111] border border-white/6 rounded-xl px-4 py-3">
                  <p className="text-white font-semibold text-sm mb-0.5">{right}</p>
                  <p className="text-zinc-500 text-xs">{desc}</p>
                </div>
              ))}
            </div>
            <p>
              Om een recht uit te oefenen, stuur een e-mail naar{" "}
              <a href="mailto:info@fitsmith.nl" className="text-[#C9A84C] hover:underline">info@fitsmith.nl</a>.
              Wij reageren binnen 30 dagen. Je hebt ook het recht een klacht in te dienen bij de{" "}
              <a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer"
                className="text-[#C9A84C] hover:underline">Autoriteit Persoonsgegevens</a>.
            </p>
          </Section>

          <Section nr="7" title="Cookies">
            <p>
              Onze website maakt gebruik van cookies — kleine tekstbestanden die in je browser worden opgeslagen. Wij gebruiken:
            </p>
            <ul className="space-y-1.5 list-none">
              {[
                "Functionele cookies: noodzakelijk voor de werking van de website (inlogstatus, voorkeuren)",
                "Analytische cookies: anoniem inzicht in websitegebruik (PostHog — geanonimiseerd)",
                "Geen tracking- of advertentiecookies van derden",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p>
              Je kunt cookies uitschakelen via je browserinstellingen, maar dit kan de werking van de website beïnvloeden.
            </p>
          </Section>

          <Section nr="8" title="Wijzigingen in dit beleid">
            <p>
              FitSmith behoudt zich het recht voor dit privacybeleid aan te passen. Wijzigingen worden op deze pagina gepubliceerd met een bijgewerkte versiedatum. Wij raden je aan dit beleid periodiek te raadplegen.
            </p>
          </Section>

          <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl px-6 py-5">
            <p className="text-white font-semibold text-sm mb-1">Contact</p>
            <p className="text-zinc-400 text-sm">
              Heb je vragen of wil je een recht uitoefenen? Neem contact op via{" "}
              <a href="mailto:info@fitsmith.nl" className="text-[#C9A84C] hover:underline font-semibold">info@fitsmith.nl</a>.
              We reageren binnen 30 dagen.
            </p>
          </div>
        </div>

        <button onClick={() => navigate(-1)}
          className="mt-12 flex items-center gap-2 text-zinc-500 hover:text-[#C9A84C] text-sm transition-colors">
          <ArrowLeft size={14} /> Terug
        </button>
      </div>

      <div className="border-t border-white/5 py-6 text-center">
        <p className="text-zinc-700 text-xs">© 2026 FitSmith. Alle rechten voorbehouden.</p>
      </div>
    </div>
  );
}
